/**
 * Projekt-Regal: die oberste Ebene der App.
 *
 * Hier wählt man die Band oder das Projekt. Songs, Setlists und Notizen
 * gehören immer zu genau einem davon.
 *
 * Aufbau wie ein Bücherregal:
 *   - Oben eine schmale Leiste mit Hell/Dunkel und dem Plus für ein neues
 *     Projekt. Beides gilt für das ganze Regal, deshalb steht es dort und
 *     nicht bei einem einzelnen Buch.
 *   - In der Mitte der Name des gewählten Projekts.
 *   - Darunter die Einbände. Gewischt wird waagerecht, das Einrasten macht
 *     CSS, die Mitte ermittelt der Code beim Scrollen.
 *   - Am gewählten Buch sitzt oben rechts ein kleiner Knopf. Er öffnet die
 *     Einstellungen GENAU DIESES Projekts: umbenennen, Einband, Sichtbarkeit,
 *     löschen.
 *
 * Zwei Dinge liegen bewusst NICHT hier:
 *   - Der Übergang Regal <-> Projekt steht in ui/book-transition.ts, weil
 *     Hinweg und Rückweg dieselbe Geometrie brauchen und der Rückweg im
 *     Rahmen beginnt (app.ts), nicht in dieser Ansicht.
 *   - Der Standard "neue Projekte sind privat" steht in emptyProject()
 *     (db/repo.ts). Der Dialog hier liest ihn nur aus, statt ihn ein
 *     zweites Mal zu behaupten.
 */
import type { View } from '../app.ts';
import { navigate, refresh } from '../app.ts';
import type { Project, ProjectVisibility } from '../types.ts';
import { COVERS, renderProjectCover } from '../ui/cover.ts';
import { armBookOpen, finishBookClose } from '../ui/book-transition.ts';
import { createSwipeRail } from '../ui/swipe-rail.ts';
import type { SwipeRail } from '../ui/swipe-rail.ts';
import {
  PROJECT_VISIBILITIES,
  deleteProject,
  emptyProject,
  ensureProjects,
  getSettings,
  listReleases,
  listMyCovers,
  listProjects,
  listSetlists,
  listSongs,
  projectVisibility,
  saveProject,
  saveSettings,
  setThemeId,
  visibilityLabel,
  withMyCover,
} from '../db/repo.ts';
import { applyTheme, resolveTheme } from '../theme/themes.ts';
import { button, field, h, textInput } from '../ui/dom.ts';
import { icon } from '../ui/icons.ts';
import { sharingApi } from '../platform/sharing-api.ts';
import { openShareDialog } from '../features/sharing/share-dialog.ts';
import { toast } from '../ui/toast.ts';
import { authProvider, can, listUsers } from '../features/auth/session.ts';
import { exportProject } from '../features/pdf/dialog.ts';
import { appUrl } from '../platform/endpoints.ts';

/** Kurzer Umfang: "12 Songs · 2 Setlists". */
function summary(counts: { songs: number; setlists: number; releases: number }): string {
  const parts: string[] = [];
  parts.push(`${counts.songs} ${counts.songs === 1 ? 'Song' : 'Songs'}`);
  if (counts.releases > 0) {
    parts.push(`${counts.releases} ${counts.releases === 1 ? 'Release' : 'Releases'}`);
  }
  if (counts.setlists > 0) {
    parts.push(`${counts.setlists} ${counts.setlists === 1 ? 'Setlist' : 'Setlists'}`);
  }
  return parts.join(' · ');
}

export async function renderProjects(): Promise<View> {
  await ensureProjects();
  const [projects, settings, songs, setlists, releases, myCovers] = await Promise.all([
    listProjects(),
    getSettings(),
    listSongs(),
    listSetlists(),
    listReleases(),
    listMyCovers(),
  ]);

  const countFor = (id: string) => ({
    songs: songs.filter((s) => s.projectId === id).length,
    setlists: setlists.filter((s) => s.projectId === id).length,
    releases: releases.filter((r) => r.projectId === id).length,
  });

  let body: HTMLElement;
  const titleEl = h('h2', { class: 'shelf__title' });
  const countEl = h('p', { class: 'shelf__count' });
  // Die Spur traegt die Buecher und wird verschoben; die Schiene darum
  // schneidet nur ab. Kein natives Scrollen: die Physik machen wir selbst.
  const track = h('div', { class: 'shelf__track' });
  const rail = h(
    'div',
    {
      class: 'shelf__rail',
      // Fokussierbar, damit die Pfeiltasten ankommen.
      attrs: { tabindex: '0', role: 'group', 'aria-label': 'Projekte' },
    },
    track,
  );

  let active = Math.max(
    0,
    projects.findIndex((p) => p.id === settings.activeProjectId),
  );

  /* ------------------------------------------------------- Kopfleiste */

  /** Schaltet zwischen hellem und dunklem Design um. */
  const themeButton = h('button', {
    class: 'shelf__round shelf__round--small',
    attrs: { type: 'button' },
  });

  function paintThemeButton(): void {
    const dark = resolveTheme(settings.themeId).id === 'dark';
    themeButton.textContent = '';
    // Das Symbol zeigt, wohin der Tipper führt.
    themeButton.append(icon(dark ? 'sun' : 'moon'));
    const label = dark ? 'Helles Design' : 'Dunkles Design';
    themeButton.setAttribute('title', label);
    themeButton.setAttribute('aria-label', label);
  }

  themeButton.addEventListener('click', () => {
    const next = resolveTheme(settings.themeId).id === 'dark' ? 'paper' : 'dark';
    settings.themeId = next;
    applyTheme(next);
    void setThemeId(next);
    // Die Seitenleiste hört mit, damit ihr Schalter dasselbe zeigt.
    window.dispatchEvent(new CustomEvent<string>('leadsheet:theme', { detail: next }));
    paintThemeButton();
  });
  paintThemeButton();

  /*
   * Admin-Knopf, nur fuer Administratoren. Neue Anfragen zeigt ein Punkt mit
   * Zahl, damit niemand lange auf die Freigabe wartet.
   */
  let adminButton: HTMLElement | null = null;
  // Loeschen: Ersteller ab Stufe 3, Administratoren jedes Projekt (firestore.rules).
  const isAdmin = await can('admin.view');
  const mayDelete = await can('content.delete');
  if (isAdmin) {
    const requests = authProvider().authenticated ? (await listUsers()).filter((u) => !u.active && !u.blocked).length : 0;
    const label = requests > 0 ? `Admin – ${requests} neue Anfrage${requests === 1 ? '' : 'n'}` : 'Admin';
    adminButton = h(
      'button',
      {
        class: 'shelf__round shelf__round--small shelf__admin',
        attrs: { type: 'button', title: label, 'aria-label': label },
        on: { click: () => location.assign(appUrl('system')) },
      },
      icon('wrench'),
      requests > 0 ? h('span', { class: 'shelf__badge', text: String(requests) }) : null,
    );
  }

  const bar = h(
    'header',
    { class: 'shelf__bar' },
    // Links oben: wo man in der Hierarchie steht –
    // Schnittstelle Rot › Build › Lead Sheets, darunter die Ansicht (Projekte).
    // Jedes Glied fuehrt an seinen Platz (platform/endpoints.ts).
    h(
      'div',
      { class: 'shelf__where' },
      h(
        'nav',
        { class: 'shelf__sr', attrs: { 'aria-label': 'Pfad' } },
        h(
          'a',
          { class: 'shelf__sr-link', attrs: { href: appUrl('home'), title: 'Schnittstelle Rot – Übersicht über Cloud, Build und System' } },
          h('span', { class: 'shelf__sr-mark', attrs: { 'aria-hidden': 'true' } }),
          h('span', { text: 'Schnittstelle Rot' }),
        ),
        h('span', { class: 'shelf__sr-sep', text: '›', attrs: { 'aria-hidden': 'true' } }),
        h('a', { class: 'shelf__sr-link', text: 'Build', attrs: { href: appUrl('cloud', '#/builds'), title: 'Alle Builds' } }),
        h('span', { class: 'shelf__sr-sep', text: '›', attrs: { 'aria-hidden': 'true' } }),
        h('span', { class: 'shelf__sr-here', text: 'Lead Sheets', attrs: { 'aria-current': 'page' } }),
      ),
      h('span', { class: 'shelf__brand', text: 'Projekte' }),
    ),
    adminButton,
    themeButton,
    h(
      'button',
      {
        class: 'shelf__round',
        attrs: { type: 'button', title: 'Neues Projekt', 'aria-label': 'Neues Projekt' },
        on: { click: () => void create() },
      },
      icon('plus'),
    ),
  );

  /* ------------------------------------------------------------ Regal */

  function showActive(): void {
    const project = projects[active];
    if (!project) {
      titleEl.textContent = 'Noch kein Projekt';
      countEl.textContent = 'Leg deine Band oder dein Projekt an.';
      return;
    }
    titleEl.textContent = project.name || 'Ohne Namen';
    const info = project.subtitle || summary(countFor(project.id));
    // Nur "Geteilt"/"Öffentlich" wird genannt: privat ist der Normalfall und
    // braucht keine Beschriftung, die bei jedem Projekt mitläuft.
    const others = (project.members?.length ?? 1) - 1;
    const shared = sharingApi()
      ? others > 0
        ? ` · Geteilt mit ${others}`
        : ''
      : projectVisibility(project) === 'public'
        ? ' · Öffentlich'
        : '';
    countEl.textContent = `${info}${shared} · zum Öffnen antippen`;

    Array.from(track.children).forEach((child, index) => {
      const el = child as HTMLElement;
      el.classList.toggle('shelf__item--active', index === active);
      // Die seitlichen Bücher stehen leicht schräg, das gibt Tiefe.
      el.classList.toggle('shelf__item--left', index < active);
      el.classList.toggle('shelf__item--right', index > active);
    });
  }

  function buildRail(): void {
    track.textContent = '';
    projects.forEach((project, index) => {
      /** Öffnet die Einstellungen genau dieses Projekts. */
      const gear = h(
        'button',
        {
          class: 'shelf__gear',
          attrs: {
            type: 'button',
            title: 'Projekt anpassen',
            'aria-label': `${project.name || 'Ohne Namen'} anpassen`,
          },
          on: {
            click: (ev) => {
              ev.stopPropagation();
              openProjectMenu(project, index, gear);
            },
          },
        },
        icon('sliders'),
      );

      const item = h(
        'div',
        {
          class: 'shelf__item',
          attrs: { role: 'button', tabindex: '0', 'aria-label': project.name || 'Ohne Namen' },
          on: {
            click: () => {
              // Nach einem Wisch ist der Klick nur das Ende der Geste.
              if (railApi?.swiped()) return;
              // Erster Tipper wählt aus, der zweite öffnet.
              if (index === active) void open(project);
              else goTo(index);
            },
          },
        },
        h(
          'div',
          {
            class: 'shelf__coverwrap',
            // Rechtsklick: dasselbe Menü wie das Zahnrad, an der Mausposition.
            on: {
              contextmenu: (ev) => {
                ev.preventDefault();
                openProjectMenu(project, index, gear, ev.clientX, ev.clientY);
              },
            },
          },
          renderProjectCover(withMyCover(project, myCovers.get(project.id))),
          gear,
        ),
        h('span', { class: 'shelf__name', text: project.name || 'Ohne Namen' }),
      );
      track.append(item);
    });
  }

  /*
   * Wischen im Regal.
   *
   * Dieselbe Physik wie bei den Veroeffentlichungen (ui/swipe-rail.ts): frei
   * und fluessig mit dem Finger, Gummiband an den Enden, und beim Loslassen
   * entscheidet der Schwung ueber Ziel UND Tempo des Einschwingens – kein
   * zweites, eigenes Regal-spezifisches Wisch-System mehr.
   */
  let railApi: SwipeRail | null = null;

  /** Schiebt das gewählte Buch in die Mitte, ohne Schwung (z. B. beim Aufbau). */
  function centerActive(smooth: boolean): void {
    railApi?.goTo(active, smooth);
  }

  /** Wechselt zum Buch an `index` und laesst es einschwingen. */
  function goTo(index: number): void {
    railApi?.goTo(index);
  }

  /* -------------------------------------------- Menü eines Projekts */

  let openMenu: HTMLElement | null = null;

  function closeMenu(): void {
    openMenu?.remove();
    openMenu = null;
  }

  function openProjectMenu(project: Project, index: number, anchor: HTMLElement, x?: number, y?: number): void {
    closeMenu();
    active = index;
    showActive();

    const row = (label: string, iconName: string, onClick: () => void, danger = false) =>
      h(
        'button',
        {
          class: 'shelf-menu__item' + (danger ? ' shelf-menu__item--danger' : ''),
          attrs: { type: 'button' },
          on: {
            click: () => {
              closeMenu();
              onClick();
            },
          },
        },
        icon(iconName),
        h('span', { text: label }),
      );

    const menu = h(
      'div',
      { class: 'shelf-menu', attrs: { role: 'menu', 'aria-label': project.name } },
      h('p', { class: 'shelf-menu__title', text: project.name || 'Ohne Namen' }),
      row('Öffnen', 'open', () => void open(project)),
      row('Umbenennen', 'pencil', () => void rename(project)),
      row('Einband gestalten', 'droplet', () => navigate(`#/cover/${project.id}`)),
      row('Als PDF exportieren', 'download', () => void exportProject(project)),
      // Mit Cloud: Teilen je Person (Freigaben). Ohne: Privat/Öffentlich.
      sharingApi()
        ? row('Teilen …', 'user', () => void openShareDialog(project, refresh))
        : // Zeigt den JETZIGEN Stand und schaltet auf den anderen um – so ist
          // ohne zweiten Dialog zu sehen, woran man ist.
          row(
            projectVisibility(project) === 'public' ? 'Auf Privat stellen' : 'Auf Öffentlich stellen',
            // Das Schloss zeigt das ERGEBNIS des Tippens, passend zur Beschriftung.
            projectVisibility(project) === 'public' ? 'lock' : 'unlock',
            () => void toggleVisibility(project),
          ),
      // Mit Freigaben: der Ersteller ab Stufe 3 – oder ein Administrator (so pruefen es auch die Regeln).
      isAdmin || (mayDelete && (!sharingApi() || project.members?.find((m) => m.role === 'owner')?.id === sharingApi()?.me))
        ? row('Projekt löschen', 'trash', () => void remove(project), true)
        : null,
    );

    document.body.append(menu);

    // Unter dem Knopf platzieren, aber im Bild halten.
    const box = anchor.getBoundingClientRect();
    const size = menu.getBoundingClientRect();
    const left = Math.min(Math.max(8, x ?? box.right - size.width), window.innerWidth - size.width - 8);
    const top = Math.min(y ?? box.bottom + 8, window.innerHeight - size.height - 8);
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
    openMenu = menu;
  }

  const onOutside = (ev: PointerEvent) => {
    if (!openMenu) return;
    if (openMenu.contains(ev.target as Node)) return;
    closeMenu();
  };
  document.addEventListener('pointerdown', onOutside);

  /* ----------------------------------------------------------- Aktionen */

  /**
   * Ein neues Projekt anlegen.
   *
   * Statt dreier prompt()-Fenster hintereinander EIN Blatt mit allem, was zum
   * Anlegen gehoert: Name, zweite Zeile und Sichtbarkeit. Die Sichtbarkeit
   * gehoert hierher und nicht in eine spaetere Einstellung – sie ist eine
   * Entscheidung ueber das Projekt, keine Nachbesserung. Sie steht auf
   * "Privat", und zwar durch emptyProject(), nicht durch eine zweite Regel
   * in der Oberflaeche.
   */
  async function create(): Promise<void> {
    const draft = emptyProject();
    let visibility: ProjectVisibility = projectVisibility(draft);

    const nameInput = textInput({ placeholder: 'z. B. Quartett Nord' });
    const subtitleInput = textInput({ placeholder: 'z. B. Besetzung oder Anlass' });

    /*
     * Die Sichtbarkeit als Segmentschalter – dasselbe Bedienelement wie beim
     * Design in der Seitenleiste. Zwei sich ausschliessende Moeglichkeiten
     * nebeneinander, beide lesbar, ohne dass der Dialog dadurch waechst.
     */
    const group = h('div', {
      class: 'segmented',
      attrs: { role: 'group', 'aria-label': 'Sichtbarkeit' },
    });
    const hint = h('p', { class: 'hint' });
    const options = PROJECT_VISIBILITIES.map((option) => {
      const el = h('button', {
        class: 'segmented__item',
        text: option.label,
        attrs: { type: 'button' },
        on: {
          click: () => {
            visibility = option.value;
            markVisibility();
          },
        },
      });
      group.append(el);
      return { value: option.value, hint: option.hint, el };
    });

    function markVisibility(): void {
      for (const option of options) {
        const on = option.value === visibility;
        option.el.classList.toggle('segmented__item--on', on);
        option.el.setAttribute('aria-pressed', String(on));
        if (on) hint.textContent = option.hint;
      }
    }
    markVisibility();

    const modal = h(
      'div',
      { class: 'modal', attrs: { role: 'dialog', 'aria-modal': 'true' } },
      h(
        'div',
        { class: 'modal__panel' },
        h(
          'div',
          { class: 'modal__head' },
          h('h2', { class: 'modal__title', text: 'Neues Projekt' }),
          button('Abbrechen', () => close(), { class: 'btn btn--small' }),
        ),
        h(
          'div',
          { class: 'modal__body' },
          field('Name', nameInput),
          field('Beschreibung', subtitleInput, 'Zweite Zeile im Regal. Darf leer bleiben.'),
          // Mit Cloud: neu ist privat (nur du); geteilt wird danach über „Teilen …“.
          sharingApi()
            ? h('p', { class: 'hint', text: 'Neue Projekte siehst nur du. Teilen kannst du danach über das Menü des Projekts („Teilen …“).' })
            : h(
                'div',
                { class: 'field' },
                h('span', { class: 'field__label', text: 'Sichtbarkeit' }),
                group,
                hint,
              ),
        ),
        h(
          'div',
          { class: 'modal__foot' },
          button('Projekt erstellen', () => void submit(), { class: 'btn btn--primary' }),
        ),
      ),
    );

    function close(): void {
      modal.remove();
    }

    async function submit(): Promise<void> {
      close();
      // Der Einband wechselt reihum, damit sich zwei Projekte nicht gleichen.
      const cover = projects.length % COVERS.length;
      const project = await saveProject(
        emptyProject({
          ...draft,
          name: nameInput.value.trim(),
          subtitle: subtitleInput.value.trim(),
          cover,
          visibility,
        }),
      );
      await saveSettings({ ...settings, activeProjectId: project.id });
      refresh();
    }

    // Tippen auf den Hintergrund bricht ab – wie in den uebrigen Dialogen.
    modal.addEventListener('click', (ev) => {
      if (ev.target === modal) close();
    });
    // Eingabetaste im Namensfeld legt an, das ist der haeufigste Weg.
    nameInput.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') void submit();
    });

    document.body.append(modal);
    nameInput.focus();
  }

  /** Privat <-> Öffentlich umschalten, ohne weiteren Dialog. */
  async function toggleVisibility(project: Project): Promise<void> {
    const next: ProjectVisibility = projectVisibility(project) === 'public' ? 'private' : 'public';
    await saveProject({ ...project, visibility: next });
    toast(`Sichtbarkeit: ${visibilityLabel(next)}`);
    refresh();
  }

  async function rename(project: Project): Promise<void> {
    const name = prompt('Name', project.name);
    if (name === null) return;
    const subtitle = prompt('Zweite Zeile, z. B. Besetzung (darf leer bleiben)', project.subtitle);
    await saveProject({ ...project, name: name.trim(), subtitle: (subtitle ?? '').trim() });
    refresh();
  }

  async function remove(project: Project): Promise<void> {
    const counts = countFor(project.id);
    const warning =
      counts.songs + counts.setlists + counts.releases > 0
        ? `\n\nDamit werden auch ${summary(counts)} gelöscht.`
        : '';
    if (!confirm(`"${project.name || 'Ohne Namen'}" löschen?${warning}`)) return;
    await deleteProject(project.id);
    if (settings.activeProjectId === project.id) {
      await saveSettings({ ...settings, activeProjectId: null });
    }
    toast('Projekt gelöscht');
    refresh();
  }

  /* ------------------------------------------------- Buch aufschlagen */

  /**
   * Was hier passiert, steht in ui/book-transition.ts – dort liegen Hinweg
   * UND Rückweg beieinander, weil beide dieselbe Geometrie brauchen. Das
   * Regal liefert nur, was es weiss: welches Buch gemeint ist.
   */
  function bookTarget() {
    const item = (track.children[active] as HTMLElement | undefined) ?? null;
    return { cover: (item?.querySelector('.cover') as HTMLElement | null) ?? null };
  }

  async function open(project: Project): Promise<void> {
    closeMenu();
    /*
     * Reihenfolge mit Absicht: erst die Bühne stellen, dann navigieren.
     *
     * armBookOpen misst den Einband, solange er noch im Regal steht, und
     * sagt dem Router, dass die kommende Ansicht als Seite INS BUCH gebaut
     * wird statt nach #app. Der Router schlägt danach selbst auf, sobald die
     * Seite steht. Hier wird deshalb nichts mehr abgewartet – es gibt keinen
     * Umschaltpunkt, auf den man warten müsste.
     */
    armBookOpen(bookTarget());
    await saveSettings({ ...settings, activeProjectId: project.id });
    navigate('#/');
  }

  /* ---------------------------------------------------------- Aufbau */

  buildRail();
  showActive();
  railApi = createSwipeRail({
    rail,
    track,
    align: 'center',
    // Auf dem Zahnrad und im Menue wird nicht gewischt.
    ignore: (target) => target.closest('.shelf__gear, .shelf-menu') !== null,
    onIndexChange: (index) => {
      active = index;
      showActive();
    },
  });
  /*
   * Zwei Bilder warten, dann erst den Rückweg zu Ende führen.
   *
   * Das erste schiebt das gewählte Buch an seinen Platz, das zweite misst
   * ihn. Vorher stünde das Buch noch am Anfang der Spur, und das
   * zurückkehrende Buch würde dorthin fahren statt in die Mitte.
   * Ist gar kein Rückweg unterwegs, tut finishBookClose nichts.
   */
  requestAnimationFrame(() => {
    centerActive(false);
    requestAnimationFrame(() => finishBookClose(bookTarget()));
  });

  const empty = h(
    'div',
    { class: 'shelf__empty' },
    h('p', { class: 'empty__title', text: 'Noch kein Projekt' }),
    h('p', { class: 'muted', text: 'Eine Band, ein Programm, ein Duo – such dir was aus.' }),
    button('+ Projekt anlegen', () => void create(), { class: 'btn btn--primary' }),
  );

  body = h(
    'div',
    { class: 'app shelf' },
    bar,
    h('div', { class: 'shelf__head' }, titleEl, countEl),
    projects.length === 0 ? empty : rail,
  );

  return {
    title: 'Projekte',
    fullscreen: true,
    body,
    // Auf dem Regal wird nicht gezeichnet, hier gibt es nichts anzumerken.
    drawing: null,
    cleanup: () => {
      railApi?.destroy();
      document.removeEventListener('pointerdown', onOutside);
      closeMenu();
    },
  };
}
